import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Plus, Trash2, Search, RefreshCw } from "react-feather";
import { Link } from "react-router";
import { useGet, usePost, usePut, useDelete } from "../../api";
import { IQaReviewListsResponse, IUser } from "../../interfaces";
import { settingsType, useSettings } from "../../hooks";
import { format } from "date-fns";
import { DomainGeoPicker, DomainGeoItem } from "./domain-geo-picker";

export const QaLists: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useSettings(settingsType.QA_LISTS_SEARCH);
  const [assigneeFilter, setAssigneeFilter] = useSettings(
    settingsType.QA_LISTS_ASSIGNEE
  );
  const [sortBy, setSortBy] = useSettings(settingsType.QA_LISTS_SORT_BY);

  // Fetch lists
  const buildUrl = useCallback(() => {
    let url = `qa-lists?page=${page}&limit=${limit}&sortBy=${sortBy || "createdAt"}&sortOrder=desc`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (assigneeFilter)
      url += `&assignee=${encodeURIComponent(assigneeFilter)}`;
    return url;
  }, [page, limit, search, assigneeFilter, sortBy]);

  const {
    data: listsData,
    loading,
    refetch,
  } = useGet<IQaReviewListsResponse>(buildUrl(), true);

  // Fetch users for assignee dropdown
  const { data: users } = useGet<IUser[]>("qa-lists/users/all", true);

  // Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newItems, setNewItems] = useState<DomainGeoItem[]>([]);

  const createList = usePost<{ _id: string }, any>();
  const deleteList = useDelete<{ success: boolean }>();
  const updateList = usePut<any, any>();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const toggleFlag = async (
    listId: string,
    patch: {
      inPipeline?: boolean;
      inOpenDataSweep?: boolean;
      dailyRecheck?: boolean;
    }
  ) => {
    setTogglingId(listId);
    try {
      await updateList.execute(`qa-lists/${listId}`, patch);
      await refetch();
    } catch (err) {
      console.error("Failed to toggle list flag:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = async () => {
    if (!newName || newItems.length === 0) return;
    try {
      await createList.execute("qa-lists", {
        name: newName,
        ...(newAssignee ? { assignee: newAssignee } : {}),
        items: newItems,
      });
      setShowCreate(false);
      setNewName("");
      setNewAssignee("");
      setNewItems([]);
      refetch();
    } catch (err) {
      console.error("Failed to create list:", err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this list?")) return;
    try {
      await deleteList.execute(`qa-lists/${id}`);
      refetch();
    } catch (err) {
      console.error("Failed to delete list:", err);
    }
  };

  const handleSearch = () => {
    setPage(1);
    refetch();
  };

  useEffect(() => {
    refetch();
  }, [page, sortBy, assigneeFilter]);

  return (
    <>
      <h3 className="mb-3">QA Review Lists</h3>

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="small mb-1">Search</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Assignee</Form.Label>
              <Form.Select
                size="sm"
                value={assigneeFilter}
                onChange={(e) => {
                  setAssigneeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All</option>
                {users?.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.username}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small mb-1">Sort By</Form.Label>
              <Form.Select
                size="sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="createdAt">Created Date</option>
                <option value="lastActivity">Last Activity</option>
                <option value="progress">Progress</option>
              </Form.Select>
            </Col>
            <Col md="auto">
              <Button size="sm" variant="primary" onClick={handleSearch}>
                <Search size={14} className="me-1" />
                Search
              </Button>
            </Col>
            <Col md="auto">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => refetch()}
              >
                <RefreshCw size={14} />
              </Button>
            </Col>
            <Col md="auto" className="ms-auto">
              <Button
                size="sm"
                variant="success"
                onClick={() => setShowCreate(true)}
              >
                <Plus size={14} className="me-1" />
                Create List
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lists table */}
      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <Card>
          <Table hover responsive className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Assignee</th>
                <th>Progress</th>
                <th>Created</th>
                <th>Last Activity</th>
                <th title="Items are fair game for the Work Pipeline">
                  Pipeline
                </th>
                <th title="Brands reopen on the Work Pipeline every day at 07:00 UTC for a code check, then an APK test">
                  Daily
                </th>
                <th title="Items are swept by the Open Data readiness run">
                  Open Data
                </th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {listsData?.results?.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No QA lists yet. Create one to get started.
                  </td>
                </tr>
              )}
              {listsData?.results?.map((list) => {
                const progressPct =
                  list.totalItems > 0
                    ? Math.round((list.testedItems / list.totalItems) * 100)
                    : 0;
                return (
                  <tr key={list._id}>
                    <td>
                      <Link
                        to={`/qa-lists/${list._id}`}
                        className="text-decoration-none fw-semibold"
                      >
                        {list.name}
                      </Link>
                    </td>
                    <td className="text-muted">{list.assigneeName}</td>
                    <td style={{ minWidth: 160 }}>
                      <div className="d-flex align-items-center gap-2">
                        <ProgressBar
                          now={progressPct}
                          variant={
                            progressPct === 100
                              ? "success"
                              : progressPct > 50
                                ? "info"
                                : "warning"
                          }
                          style={{ flex: 1, height: 8 }}
                        />
                        <small className="text-muted">
                          {list.testedItems}/{list.totalItems}
                        </small>
                      </div>
                    </td>
                    <td className="text-muted small">
                      {list.createdAt
                        ? format(new Date(list.createdAt), "yyyy-MM-dd")
                        : "—"}
                    </td>
                    <td className="text-muted small">
                      {list.lastActivity
                        ? format(
                            new Date(list.lastActivity),
                            "yyyy-MM-dd HH:mm"
                          )
                        : "—"}
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`pipe-${list._id}`}
                        checked={!!list.inPipeline}
                        disabled={togglingId === list._id}
                        onChange={() =>
                          toggleFlag(list._id, {
                            inPipeline: !list.inPipeline,
                          })
                        }
                        label={list.inPipeline ? "On" : "Off"}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`daily-${list._id}`}
                        checked={!!list.dailyRecheck}
                        disabled={togglingId === list._id || !list.inPipeline}
                        title={
                          list.inPipeline
                            ? "Reopen these brands every day for a code check and APK test"
                            : "Turn Pipeline on first"
                        }
                        onChange={() =>
                          toggleFlag(list._id, {
                            dailyRecheck: !list.dailyRecheck,
                          })
                        }
                        label={list.dailyRecheck ? "On" : "Off"}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        id={`opendata-${list._id}`}
                        checked={!!list.inOpenDataSweep}
                        disabled={togglingId === list._id}
                        onChange={() =>
                          toggleFlag(list._id, {
                            inOpenDataSweep: !list.inOpenDataSweep,
                          })
                        }
                        label={list.inOpenDataSweep ? "On" : "Off"}
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(list._id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {listsData && listsData.pagination.totalPages > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-3">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="align-self-center small text-muted">
            Page {page} of {listsData.pagination.totalPages}
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={page >= listsData.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create QA Review List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>List Name</Form.Label>
            <Form.Control
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. John — UK merchants Q2"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Assignee</Form.Label>
            <Form.Select
              value={newAssignee}
              onChange={(e) => setNewAssignee(e.target.value)}
            >
              <option value="">Unassigned</option>
              {users?.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>
              Domain / Country Combinations ({newItems.length} selected)
            </Form.Label>
            <DomainGeoPicker selected={newItems} onChange={setNewItems} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleCreate}
            disabled={!newName || newItems.length === 0 || createList.loading}
          >
            {createList.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Create List"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
